import { Link } from "@inertiajs/react"

import type { EvidenceTypeOption, InitialEntryData } from "./new"

interface Option {
  id: number
  name: string
}

interface ReportFormData {
  report_type: number
  category: number
  report_date: string
  notes: string
  admin_notes: string
  significant: number
  vendor_id: number | null
  investigator_name: string
}

interface Props {
  caseId: number
  data: ReportFormData
  setData: (key: keyof ReportFormData, value: string | number | null) => void
  errors?: Record<string, string[]>
  processing: boolean
  onSubmit: (e: React.FormEvent) => void
  submitLabel: string
  users: Option[]
  vendors: Option[]
  isNew?: boolean
  teamMembers?: Option[]
  teamMemberIds?: number[]
  onTeamMemberToggle?: (id: number) => void
  initialEntry?: InitialEntryData
  onInitialEntryChange?: (
    key: keyof InitialEntryData,
    value: InitialEntryData[keyof InitialEntryData],
  ) => void
  evidenceTypes?: EvidenceTypeOption[]
  onEvidenceTypeToggle?: (id: number) => void
  setTeamLeaderId?: (id: number | null) => void
}

export default function ReportForm({
  caseId,
  data,
  setData,
  errors,
  processing,
  onSubmit,
  submitLabel,
  users,
  vendors,
  isNew,
  teamMembers,
  teamMemberIds,
  onTeamMemberToggle,
  initialEntry,
  onInitialEntryChange,
  evidenceTypes,
  onEvidenceTypeToggle,
  setTeamLeaderId,
}: Props) {
  const reportTypes = [
    { value: 0, label: "Original" },
    { value: 1, label: "Revision" },
  ]

  const categories = [
    { value: 0, label: "Daily" },
    { value: 1, label: "GPS" },
    { value: 3, label: "Incident" },
  ]

  const sortedTeamMembers = teamMembers
    ? [...teamMembers].sort((a, b) => a.name.localeCompare(b.name))
    : []

  return (
    <form onSubmit={onSubmit} className="max-w-3xl">
      <div className="mb-4 grid gap-4 md:grid-cols-2">
        <div>
          <label htmlFor="category" className="mb-1 block font-medium">
            Category
          </label>
          <select
            id="category"
            value={data.category}
            onChange={(e) => setData("category", parseInt(e.target.value))}
            className="border-hpi-light-gray w-full rounded border px-3 py-2"
          >
            {categories.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
          {errors?.category && (
            <p className="text-hpi-red mt-1 text-sm">
              {errors.category.join(", ")}
            </p>
          )}
        </div>
        {!isNew && (
          <div>
            <label htmlFor="report_type" className="mb-1 block font-medium">
              Report Type
            </label>
            <select
              id="report_type"
              value={data.report_type}
              onChange={(e) => setData("report_type", parseInt(e.target.value))}
              className="border-hpi-light-gray w-full rounded border px-3 py-2"
            >
              {reportTypes.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
            {errors?.report_type && (
              <p className="text-hpi-red mt-1 text-sm">
                {errors.report_type.join(", ")}
              </p>
            )}
          </div>
        )}
      </div>

      <div className="mb-4 grid gap-4 md:grid-cols-2">
        <div>
          <label htmlFor="report_date" className="mb-1 block font-medium">
            Report Date
          </label>
          <input
            type="datetime-local"
            id="report_date"
            value={data.report_date ? data.report_date.slice(0, 16) : ""}
            onChange={(e) => setData("report_date", e.target.value)}
            className="border-hpi-light-gray w-full rounded border px-3 py-2"
          />
          {errors?.report_date && (
            <p className="text-hpi-red mt-1 text-sm">
              {errors.report_date.join(", ")}
            </p>
          )}
        </div>
        <div>
          <label htmlFor="investigator_name" className="mb-1 block font-medium">
            Team Leader
          </label>
          <select
            id="investigator_name"
            value={data.investigator_name}
            onChange={(e) => {
              const selectedUser = users.find((u) => u.name === e.target.value)
              setData("investigator_name", e.target.value)
              if (setTeamLeaderId) {
                setTeamLeaderId(selectedUser?.id ?? null)
              }
            }}
            className="border-hpi-light-gray w-full rounded border px-3 py-2"
          >
            <option value="">Select Team Leader</option>
            {users.map((u) => (
              <option key={u.id} value={u.name}>
                {u.name}
              </option>
            ))}
          </select>
          {errors?.investigator_name && (
            <p className="text-hpi-red mt-1 text-sm">
              {errors.investigator_name.join(", ")}
            </p>
          )}
        </div>
      </div>

      {!isNew && (
        <div className="mb-4">
          <label htmlFor="vendor_id" className="mb-1 block font-medium">
            Vendor
          </label>
          <select
            id="vendor_id"
            value={data.vendor_id ?? ""}
            onChange={(e) =>
              setData(
                "vendor_id",
                e.target.value ? parseInt(e.target.value) : null,
              )
            }
            className="border-hpi-light-gray w-full rounded border px-3 py-2"
          >
            <option value="">None</option>
            {vendors.map((v) => (
              <option key={v.id} value={v.id}>
                {v.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {!isNew && (
        <div className="mb-4">
          <label htmlFor="notes" className="mb-1 block font-medium">
            Notes
          </label>
          <textarea
            id="notes"
            value={data.notes}
            onChange={(e) => setData("notes", e.target.value)}
            className="border-hpi-light-gray w-full rounded border px-3 py-2"
            rows={4}
          />
          {errors?.notes && (
            <p className="text-hpi-red mt-1 text-sm">
              {errors.notes.join(", ")}
            </p>
          )}
        </div>
      )}

      {!isNew && (
        <div className="mb-4">
          <label htmlFor="admin_notes" className="mb-1 block font-medium">
            Admin Notes
          </label>
          <textarea
            id="admin_notes"
            value={data.admin_notes}
            onChange={(e) => setData("admin_notes", e.target.value)}
            className="border-hpi-light-gray w-full rounded border px-3 py-2"
            rows={3}
          />
          {errors?.admin_notes && (
            <p className="text-hpi-red mt-1 text-sm">
              {errors.admin_notes.join(", ")}
            </p>
          )}
        </div>
      )}

      <div className="mb-6">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={data.significant === 1}
            onChange={(e) => setData("significant", e.target.checked ? 1 : 0)}
            className="border-hpi-light-gray rounded"
          />
          <span className="font-medium">Significant</span>
        </label>
      </div>

      {teamMembers && onTeamMemberToggle && teamMemberIds && (
        <div className="mb-6">
          <h3 className="mb-2 text-lg font-medium">Team Members</h3>
          <div className="border-hpi-light-gray rounded border p-4">
            <div className="grid gap-2 sm:grid-cols-2 md:grid-cols-4">
              {sortedTeamMembers.map((member) => (
                <label key={member.id} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={teamMemberIds.includes(member.id)}
                    onChange={() => onTeamMemberToggle(member.id)}
                    className="border-hpi-light-gray rounded"
                  />
                  <span className="text-sm">{member.name}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      )}

      {isNew && initialEntry && onInitialEntryChange && (
        <div className="mb-6">
          <h3 className="mb-2 text-lg font-medium">Initial Report Entry</h3>
          <div className="border-hpi-light-gray rounded border p-4">
            <div className="mb-4">
              <label
                htmlFor="initial_start_time"
                className="mb-1 block font-medium"
              >
                Start Time
              </label>
              <input
                type="datetime-local"
                id="initial_start_time"
                value={
                  initialEntry.start_time
                    ? initialEntry.start_time.slice(0, 16)
                    : ""
                }
                onChange={(e) =>
                  onInitialEntryChange("start_time", e.target.value)
                }
                className="border-hpi-light-gray w-full max-w-sm rounded border px-3 py-2"
              />
            </div>

            <div className="mb-4">
              <label
                htmlFor="initial_entry_text"
                className="mb-1 block font-medium"
              >
                Entry Text
              </label>
              <textarea
                id="initial_entry_text"
                value={initialEntry.entry_text}
                onChange={(e) =>
                  onInitialEntryChange("entry_text", e.target.value)
                }
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
                rows={6}
              />
            </div>

            <div>
              <h4 className="mb-2 font-medium">Evidence</h4>
              {evidenceTypes && evidenceTypes.length > 0 ? (
                <div className="grid gap-3 md:grid-cols-4">
                  {evidenceTypes.map((type) => (
                    <label key={type.id} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={initialEntry.evidence_type_ids.includes(
                          type.id,
                        )}
                        onChange={() => onEvidenceTypeToggle?.(type.id)}
                        className="border-hpi-light-gray rounded"
                      />
                      <span>{type.name}</span>
                    </label>
                  ))}
                </div>
              ) : (
                <p className="text-hpi-med-gray text-sm">
                  No evidence types configured. Add some in Admin → Evidence
                  Types.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="flex gap-2">
        <button
          type="submit"
          disabled={processing}
          className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white disabled:opacity-50"
        >
          {submitLabel}
        </button>
        <Link
          href={`/cases/${caseId}/reports`}
          className="border-hpi-light-gray hover:bg-hpi-pale-blue rounded border px-4 py-2"
        >
          Cancel
        </Link>
      </div>
    </form>
  )
}
